import { useEffect, useMemo, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  Sparkles, Download, Copy, Star, Trash2, Loader2, Search,
  Square, RectangleHorizontal, RectangleVertical, ImageIcon,
  ChevronLeft, ChevronRight,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/contexts/AuthContext";
import {
  generatedImagesService, generatedImagesQueryKey,
  type GeneratedImage,
} from "@/services/generatedImagesService";

// OpenRouter image-generation model slugs (openrouter.ai/docs/features/multimodal/image-generation) —
// NOT OpenAI's own model names; OpenRouter has its own catalog/naming for images.
const MODELS = [
  { id: "openai/gpt-image-1", label: "GPT Image 1" },
  { id: "google/gemini-2.5-flash-image", label: "Gemini 2.5 Flash Image" },
];
const SIZES = [
  { id: "1024x1024" as const, label: "Square", Icon: Square },
  { id: "1792x1024" as const, label: "Landscape", Icon: RectangleHorizontal },
  { id: "1024x1792" as const, label: "Portrait", Icon: RectangleVertical },
];
const NOTICE_KEY = "ds_images_privacy_notice_seen"; // UI notice flag only (not auth)

/**
 * Creative Studio — AI image generation (DS EDIT 3 jul26). All generation via
 * the Deeply Smart server lane; images stored privately, signed URLs only.
 * Iceberg: prompt+generate is the tip; gallery is the shallows; search is deep.
 */
export default function CreativeStudio() {
  const { user } = useAuth();
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const [prompt, setPrompt] = useState("");
  const [model, setModel] = useState(MODELS[0].id);
  const [size, setSize] = useState<(typeof SIZES)[number]["id"]>("1024x1024");
  const [latest, setLatest] = useState<{ url: string | null; prompt: string } | null>(null);
  const [gallerySearch, setGallerySearch] = useState("");
  const [noticeSeen, setNoticeSeen] = useState(true);
  const [viewIndex, setViewIndex] = useState<number | null>(null);

  useEffect(() => {
    try { setNoticeSeen(localStorage.getItem(NOTICE_KEY) === "1"); } catch { /* ignore */ }
  }, []);
  const acknowledgeNotice = () => {
    try { localStorage.setItem(NOTICE_KEY, "1"); } catch { /* ignore */ }
    setNoticeSeen(true);
  };

  const imagesQuery = useQuery({
    queryKey: generatedImagesQueryKey(user?.id),
    queryFn: async () => generatedImagesService.withSignedUrls(await generatedImagesService.list()),
    enabled: !!user,
  });
  const images = imagesQuery.data ?? [];
  const filtered = useMemo(() => {
    const q = gallerySearch.trim().toLowerCase();
    if (!q) return images;
    return images.filter((i) => i.prompt.toLowerCase().includes(q) || i.tags.some((t) => t.toLowerCase().includes(q)));
  }, [images, gallerySearch]);
  // Index into `filtered` (not a stored object) so it stays live against query
  // refetches (favorite/delete) and drives prev/next navigation directly.
  const viewImg = viewIndex !== null ? filtered[viewIndex] ?? null : null;
  const hasPrev = viewIndex !== null && viewIndex > 0;
  const hasNext = viewIndex !== null && viewIndex < filtered.length - 1;
  const goPrev = () => setViewIndex((i) => (i !== null && i > 0 ? i - 1 : i));
  const goNext = () => setViewIndex((i) => (i !== null && i < filtered.length - 1 ? i + 1 : i));

  useEffect(() => {
    if (viewIndex === null) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "ArrowLeft") goPrev();
      else if (e.key === "ArrowRight") goNext();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewIndex, filtered.length]);

  const invalidate = () => void queryClient.invalidateQueries({ queryKey: generatedImagesQueryKey(user?.id) });

  const generateMutation = useMutation({
    mutationFn: () => generatedImagesService.generate({ prompt, model, size }),
    onSuccess: (r) => {
      setLatest({ url: r.storage_url, prompt: r.prompt });
      setPrompt("");
      invalidate();
      toast({ title: "Your image is ready" });
      if (r.crisis_language) {
        toast({
          title: "We noticed your prompt mentions something heavy",
          description: "If you're going through a hard time, the 988 Suicide & Crisis Lifeline (call or text 988) is there for you.",
        });
      }
    },
    onError: (e: Error) =>
      toast({ title: "Something didn’t work on our end. Ready to try a different prompt?", description: e.message, variant: "destructive" }),
  });

  const favoriteMutation = useMutation({
    mutationFn: (img: GeneratedImage) => generatedImagesService.setFavorite(img.id, !img.isFavorite),
    onSuccess: invalidate,
  });
  const deleteMutation = useMutation({
    mutationFn: (img: GeneratedImage) => generatedImagesService.deleteImage(img),
    onSuccess: () => { invalidate(); toast({ title: "Image removed" }); },
  });

  const download = async (url: string, name: string) => {
    const res = await fetch(url);
    const blob = await res.blob();
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `${name.slice(0, 40).replace(/[^\w-]+/g, "_") || "image"}.png`;
    a.click();
    URL.revokeObjectURL(a.href);
  };
  const copyImage = async (url: string) => {
    try {
      const res = await fetch(url);
      const blob = await res.blob();
      await navigator.clipboard.write([new ClipboardItem({ [blob.type]: blob })]);
      toast({ title: "Copied to clipboard" });
    } catch {
      await navigator.clipboard.writeText(url);
      toast({ title: "Image link copied" });
    }
  };

  return (
    <div className="p-6 max-w-4xl mx-auto space-y-5">
      <div>
        <h1 className="text-2xl font-bold flex items-center gap-2">
          <ImageIcon className="h-6 w-6" /> Creative Studio
        </h1>
        <p className="text-sm text-muted-foreground mt-1">AI-generated images from your prompts — stored privately, visible only to you.</p>
      </div>

      {!noticeSeen && (
        <Card className="border-amber-500/40">
          <CardContent className="pt-4 flex items-start justify-between gap-3 flex-wrap">
            <p className="text-sm">
              Images you create are stored privately and only visible to you. You can delete them at any time.
            </p>
            <Button size="sm" variant="outline" onClick={acknowledgeNotice}>Got it</Button>
          </CardContent>
        </Card>
      )}

      {/* Tip: prompt + generate */}
      <Card>
        <CardContent className="pt-4 space-y-3">
          <div>
            <textarea
              value={prompt}
              onChange={(e) => setPrompt(e.target.value.slice(0, 1000))}
              placeholder="Describe an image you'd like to bring to life…"
              rows={3}
              className="w-full rounded-md border border-border bg-transparent p-3 text-sm resize-y"
              data-testid="input-image-prompt"
            />
            <div className="text-[11px] text-muted-foreground text-right">{prompt.length}/1000</div>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <Select value={model} onValueChange={setModel}>
              <SelectTrigger className="w-44 h-9"><SelectValue /></SelectTrigger>
              <SelectContent>
                {MODELS.map((m) => <SelectItem key={m.id} value={m.id}>{m.label}</SelectItem>)}
              </SelectContent>
            </Select>
            <div className="flex gap-1" role="group" aria-label="Image size">
              {SIZES.map(({ id, label, Icon }) => (
                <Button key={id} size="icon" variant={size === id ? "default" : "outline"}
                  className="h-9 w-9" title={label} aria-pressed={size === id} onClick={() => setSize(id)}>
                  <Icon className="h-4 w-4" />
                </Button>
              ))}
            </div>
            <Button
              className="ml-auto bg-amber-600 hover:bg-amber-600/90 text-white"
              onClick={() => generateMutation.mutate()}
              disabled={generateMutation.isPending || !prompt.trim()}
              data-testid="button-generate-image"
            >
              {generateMutation.isPending ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Sparkles className="h-4 w-4 mr-2" />}
              Generate
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Latest generation */}
      {generateMutation.isPending && (
        <Card><CardContent className="pt-4"><Skeleton className="w-full aspect-square max-w-md mx-auto rounded-lg" /></CardContent></Card>
      )}
      {latest?.url && !generateMutation.isPending && (
        <Card>
          <CardContent className="pt-4 space-y-2">
            <img src={latest.url} alt={`Generated image: ${latest.prompt.slice(0, 80)}`}
              className="rounded-lg max-w-full max-h-[520px] mx-auto" style={{ animation: "fadeIn 600ms ease-out" }} />
            <style>{`@keyframes fadeIn { from { opacity: 0; transform: scale(0.985) } to { opacity: 1; transform: none } }`}</style>
            <p className="text-xs text-muted-foreground text-center truncate">AI-generated from your prompt: “{latest.prompt}”</p>
            <div className="flex justify-center gap-2">
              <Button size="sm" variant="outline" onClick={() => void download(latest.url!, latest.prompt)}><Download className="h-4 w-4 mr-1" />Download</Button>
              <Button size="sm" variant="outline" onClick={() => void copyImage(latest.url!)}><Copy className="h-4 w-4 mr-1" />Copy</Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Shallows: gallery */}
      <div className="flex items-center gap-2">
        <h2 className="text-base font-semibold">Your gallery {images.length ? <Badge variant="secondary" className="ml-1">{images.length}</Badge> : null}</h2>
        <div className="relative ml-auto w-56">
          <Search className="h-3.5 w-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input value={gallerySearch} onChange={(e) => setGallerySearch(e.target.value)}
            placeholder="Search prompts, tags…" className="h-8 pl-8 text-sm" />
        </div>
      </div>

      {imagesQuery.isLoading ? (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
          {[1, 2, 3, 4].map((i) => <Skeleton key={i} className="aspect-square rounded-lg" />)}
        </div>
      ) : filtered.length === 0 ? (
        <Card><CardContent className="py-10 text-center text-sm text-muted-foreground">
          {images.length === 0
            ? "Your creative studio awaits. Describe an image you'd like to bring to life."
            : "Nothing matches that search."}
        </CardContent></Card>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
          {filtered.map((img, idx) => (
            <div key={img.id} className="group relative rounded-lg overflow-hidden border border-border cursor-pointer"
              data-testid={`gallery-image-${img.id}`}
              role="button" tabIndex={0} aria-label={`View image: ${img.prompt.slice(0, 60)}`}
              onClick={() => setViewIndex(idx)}
              onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); setViewIndex(idx); } }}>
              {img.url ? (
                <img src={img.url} alt={`AI-generated: ${img.prompt.slice(0, 60)}`} className="w-full aspect-square object-cover" loading="lazy" />
              ) : (
                <div className="w-full aspect-square bg-muted" />
              )}
              <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/60 to-transparent p-2 opacity-0 group-hover:opacity-100 transition-opacity">
                <p className="text-[11px] text-white truncate">{img.prompt.slice(0, 60)}</p>
                <div className="flex gap-1 mt-1">
                  <Button size="icon" variant="ghost" className="h-6 w-6 text-white"
                    aria-pressed={img.isFavorite} aria-label={img.isFavorite ? "Unfavorite" : "Favorite"}
                    onClick={(e) => { e.stopPropagation(); favoriteMutation.mutate(img); }}>
                    <Star className={`h-3.5 w-3.5 ${img.isFavorite ? "fill-amber-400 text-amber-400" : ""}`} />
                  </Button>
                  {img.url && (
                    <Button size="icon" variant="ghost" className="h-6 w-6 text-white" aria-label="Download"
                      onClick={(e) => { e.stopPropagation(); void download(img.url!, img.prompt); }}>
                      <Download className="h-3.5 w-3.5" />
                    </Button>
                  )}
                  <Button size="icon" variant="ghost" className="h-6 w-6 text-white ml-auto" aria-label="Delete"
                    onClick={(e) => { e.stopPropagation(); if (confirm("Remove this image? This can't be undone.")) deleteMutation.mutate(img); }}>
                    <Trash2 className="h-3.5 w-3.5 text-amber-300" />
                  </Button>
                </div>
              </div>
              {img.isFavorite && <Star className="absolute top-1.5 right-1.5 h-4 w-4 fill-amber-400 text-amber-400 drop-shadow" />}
            </div>
          ))}
        </div>
      )}

      {/* Image view modal */}
      <Dialog open={!!viewImg} onOpenChange={(open) => { if (!open) setViewIndex(null); }}>
        <DialogContent className="max-w-2xl">
          {viewImg && (
            <>
              <DialogHeader>
                <DialogTitle className="truncate pr-6">{viewImg.prompt || "Generated image"}</DialogTitle>
                <DialogDescription>
                  {viewImg.model}
                  {viewImg.width && viewImg.height ? ` · ${viewImg.width}×${viewImg.height}` : ""}
                  {viewImg.createdAt ? ` · ${new Date(viewImg.createdAt).toLocaleString()}` : ""}
                  {filtered.length > 1 ? ` · ${(viewIndex ?? 0) + 1} of ${filtered.length}` : ""}
                </DialogDescription>
              </DialogHeader>

              <div className="relative flex items-center justify-center">
                <Button size="icon" variant="ghost"
                  className="absolute left-0 z-10 h-9 w-9 disabled:opacity-30"
                  aria-label="Previous image" disabled={!hasPrev} onClick={goPrev}>
                  <ChevronLeft className="h-5 w-5" />
                </Button>
                {viewImg.url ? (
                  <img src={viewImg.url} alt={`AI-generated: ${viewImg.prompt.slice(0, 80)}`}
                    className="w-full max-h-[65vh] object-contain rounded-md" />
                ) : (
                  <div className="w-full aspect-square bg-muted rounded-md" />
                )}
                <Button size="icon" variant="ghost"
                  className="absolute right-0 z-10 h-9 w-9 disabled:opacity-30"
                  aria-label="Next image" disabled={!hasNext} onClick={goNext}>
                  <ChevronRight className="h-5 w-5" />
                </Button>
              </div>

              <div className="flex items-center justify-center gap-2 flex-wrap">
                <Button size="sm" variant="outline" aria-pressed={viewImg.isFavorite}
                  onClick={() => favoriteMutation.mutate(viewImg)}>
                  <Star className={`h-4 w-4 mr-1 ${viewImg.isFavorite ? "fill-amber-400 text-amber-400" : ""}`} />
                  {viewImg.isFavorite ? "Favorited" : "Favorite"}
                </Button>
                {viewImg.url && (
                  <>
                    <Button size="sm" variant="outline" onClick={() => void download(viewImg.url!, viewImg.prompt)}>
                      <Download className="h-4 w-4 mr-1" />Download
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => void copyImage(viewImg.url!)}>
                      <Copy className="h-4 w-4 mr-1" />Copy
                    </Button>
                  </>
                )}
                <Button size="sm" variant="outline"
                  onClick={() => {
                    if (confirm("Remove this image? This can't be undone.")) {
                      deleteMutation.mutate(viewImg);
                      setViewIndex(null);
                    }
                  }}>
                  <Trash2 className="h-4 w-4 mr-1 text-amber-600" />Delete
                </Button>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
